import { FunctionComponent, useState } from 'react';
import { Button, Card, Chip, ChipProps } from '@mui/material';
import { TicketDrawerBlock } from '@/components/TicketsOverviewUI/TicketDrawerContent/TicketDrawerBlock';
import { TicketDrawerLabel } from '@/components/TicketsOverviewUI/TicketDrawerContent/TicketDrawerLabel';
import { drawerValueToString } from '@/components/TicketsOverviewUI/ticketUiUtils';
import { FlexContainer } from '@/components/Layout/Container';
import { InsuranceStatus, Ticket } from '@/models/ticket';
import { InsuranceCancelDialog } from '@/components/TicketsOverviewUI/TicketDrawerContent/InsuranceCancelDialog';
import { get } from 'lodash';

const insuranceFields = [
    ['tariffName.de', 'Produkt'],
    ['bookedAt', 'Gebucht am'],
    ['status', 'Versicherungsstatus'],
    ['revocable', 'Widerrufbar'],
    ['cancelable', 'Stornierbar'],
    ['canceledAt', 'Storniert am'],
    ['revokedAt', 'Widerrufen am'],
] as const;

type InsuranceCardProps = {
    ticket: Ticket;
};

export const InsuranceCard: FunctionComponent<InsuranceCardProps> = ({
    ticket,
}) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState<
        'cancel' | 'refund' | 'cancelFromInsurance'
    >('cancel');

    return (
        <>
            <Card>
                {insuranceFields.map(([key, value]) => (
                    <TicketDrawerBlock header={value} key={key}>
                        {key === 'status' ? (
                            <Chip
                                label={getInsuranceStatusLabel(
                                    ticket.insurance.status
                                )}
                                color={getInsuranceStatusColor(
                                    ticket.insurance.status
                                )}
                            />
                        ) : (
                            <TicketDrawerLabel>
                                {drawerValueToString(
                                    get(ticket?.insurance, key, '-')
                                )}
                            </TicketDrawerLabel>
                        )}
                    </TicketDrawerBlock>
                ))}

                <TicketDrawerBlock header='Aktion'>
                    <FlexContainer
                        sx={{
                            justifyContent: 'space-between',
                        }}
                    >
                        <Button
                            disabled={!ticket.insurance.cancelable}
                            onClick={() => {
                                setDialogType('cancel');
                                setIsDialogOpen(true);
                            }}
                            variant='contained'
                            color='error'
                        >
                            Stornieren
                        </Button>

                        <Button
                            disabled={!ticket.insurance.revocable}
                            onClick={() => {
                                setDialogType('refund');
                                setIsDialogOpen(true);
                            }}
                            variant='contained'
                            color='error'
                        >
                            Widerrufen
                        </Button>
                        <Button
                            onClick={() => {
                                setDialogType('cancelFromInsurance');
                                setIsDialogOpen(true);
                            }}
                            disabled={
                                !!ticket.insurance.canceledAt ||
                                !!ticket.insurance.revokedAt
                            }
                            variant='contained'
                            color='warning'
                        >
                            Stornierung durch Versicherer
                        </Button>
                    </FlexContainer>
                </TicketDrawerBlock>
            </Card>

            <InsuranceCancelDialog
                open={isDialogOpen}
                onClose={() => {
                    setIsDialogOpen(false);
                }}
                ticket={ticket}
                type={dialogType}
            />
        </>
    );
};

const getInsuranceStatusColor = (
    status: InsuranceStatus
): ChipProps['color'] => {
    switch (status) {
        case 'CREATED':
            return 'info';
        case 'PAID':
            return 'success';
        case 'PERMISSION_BLOCKED':
            return 'warning';
        case 'BOOKED':
            return 'success';
        case 'CANCELED':
            return 'error';
        case 'REVOKED':
            return 'error';
        default:
            return 'default';
    }
};

const getInsuranceStatusLabel = (status: InsuranceStatus) => {
    switch (status) {
        case 'CREATED':
            return 'Erstellt';
        case 'PAID':
            return 'Bezahlt';
        case 'PERMISSION_BLOCKED':
            return 'Erlaubnis blockiert';
        case 'BOOKED':
            return 'Gebucht';
        case 'CANCELED':
            return 'Storniert';
        case 'REVOKED':
            return 'Widerrufen';
        default:
            return '-';
    }
};
